import { Component, OnInit, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Dep } from '../class/auth';
import { Rtable } from '../class/rtabledata';
import { page, menulist } from '../class/status';
import * as moment from 'moment';
import Swal from 'sweetalert2';

/**
 * 經紀公司人員(工作室)：工作室成員的提領對帳。
 *
 * 跟「經紀公司人員 → 對帳」那一頁的差別：
 *  - 資料來源是 get_studio_member.php（只回 vendor_member.is_studio = 1 的提領列，一般對帳/勞報單/匯款單已排除這些人）
 *  - 多了「發票」流程：工作室每期會寄發票 → 後台上傳（type 2）→ 會計核對金額後核實（type 3）→ 才能改付款狀態（type 1）
 *    後端 set_studio_member type 1 與匯入 Excel 都會擋 invoice_ok != 1，前端這裡只是先把按鈕鎖起來。
 *  - 匯款單 / 領現單走 export_studio_wd(_cash).php，只匯出 invoice_ok = 1 的列。
 *
 * 後端不分頁；列表一次拿回來後在前端排序 + 分頁，切排序 / 翻頁 / 切欄位 / 改每頁筆數都不重打 API。
 * 排序值沿用全站慣例：'0' 大到小、'1' 小到大（r-table 手機版下拉的文字就是這樣對）。
 */
@Component({
    selector: 'app-studio-member',
    templateUrl: './studio-member.component.html',
    styleUrls: ['./studio-member.component.css']
})
export class StudioMemberComponent implements OnInit {

    static readonly LS_COLS = 'studio_member_cols';        // 'all' = 顯示全部欄位
    static readonly LS_PAGE_SIZE = 'studio_member_page_size';
    static readonly INVOICE_EXT = ['jpg', 'jpeg', 'png', 'gif']; // 後端 804b044d 起白名單只有這四種（小寫），webp 會回「非有效圖片」

    // 匯出：後端預設只出「尚未付款」的列，勾了才帶 all=1 連已付款 / 付款失敗一起出
    include_paid = false;

    preurl = environment.serverurl;

    // loading 用計數：列表重抓與逐列操作可能同時在跑，boolean 會被先回來的那個提早關掉
    private pending = 0;
    get loading(): boolean { return this.pending > 0; }
    loaded_once = false;      // 第一次列表回來前顯示「載入中…」，之後的重抓只靠整頁遮罩
    private req_seq = 0;      // 連按搜尋時，舊的回應晚到不能蓋掉新的

    // ---- 搜尋條件（參數名照 get_studio_member.php，search_trasnfer_status 的拼字是後端既有的）----
    vid = '-1';
    current_month = '';
    current_monthed = '';
    search_model = '';
    search_studio_name = '';
    search_trasnfer_status = '';      // '' 全部 / 1 匯款(1,2) / 5 領現 / 2 累積(3,4)
    search_withdrawal_status = '-1';  // -1 全部 / 0 未付款 / 1 已付款 / 2 付款失敗
    search_invoice_ok = '-1';         // -1 全部 / 0 未核實 / 1 已核實
    search_health_fee = '';           // 1 只列不需扣健保
    search_account_memo = '';         // 1 只列會計有備註

    // 團隊下拉：後端沒有另外給團隊清單，用回來的列累積 (vid → vendor_name)，只加不減
    vendor_options: menulist = [{ id: '-1', name: '全部團隊' }];

    // ---- 列表 ----
    data_all: any[] = [];   // 後端回的全部列（已套用搜尋條件）
    data: any[] = [];       // 目前這一頁
    posts_attributes: any = [];
    posts_input: any = { attribute: [], content: [], sort_obj: { column: 'real_money', sort: '0' } };
    sort_obj: { column: string, sort: string } = { column: 'real_money', sort: '0' }; // '0' 大到小、'1' 小到大
    pageobj: page = { page: 0, count: 20, total_count: 0 };
    pageobjstr: any = JSON.parse(JSON.stringify(this.pageobj));
    page_size_options = [10, 20, 50, 100];
    load_msg = '';

    // 目前查詢結果的進度統計：照會計的工作順序切成互斥的桶
    //   會出帳(ts 1/2/5)且尚未付款的列：no_invoice 待上傳發票 → awaiting 待核實 → ready 可付款(已核實)
    //   paid 已付款 / failed 付款失敗(已核實，可再付) / stuck 付款失敗但發票未核實(後端凍結發票又要核實才能付，兩邊都動不了)
    //   accum_rows 累積列：不出帳，金額滾進之後某一期，發票也不用在這裡收，所以不進待辦桶
    //   金額：unpaid_sum 未付合計(出帳列且還沒付款成功，含付款失敗，不論有沒有核實) / ready_sum 可付款合計(=匯款單會出的金額)
    //         / failed_sum 付款失敗合計 / paid_sum 已付合計
    summary = { no_invoice: 0, awaiting: 0, ready: 0, paid: 0, failed: 0, stuck: 0, unpaid_rows: 0, unpaid_sum: 0, ready_sum: 0, failed_sum: 0, paid_sum: 0, accum_rows: 0 };

    // 欄位模式：精簡（會計對帳實際會用到的 9 欄，1366 寬不用橫向捲）/ 全部（含收入拆分、銀行、身分資料）
    show_all_cols = false;

    // 發票對照視窗：大圖旁邊直接放應核金額與核實按鈕，不用另開分頁再切回來比
    preview: any = null;

    // 說明面板
    show_help = false;

    constructor(public httpClient: HttpClient) { }

    ngOnInit(): void {
        Dep.getData(); // 確保 Dep.token 已從 localStorage 載入（匯出連結、Excel 上傳直接用 Dep.token）
        this.reset_filters(false);
        try {
            this.show_all_cols = localStorage.getItem(StudioMemberComponent.LS_COLS) === 'all';
            const ps = Number(localStorage.getItem(StudioMemberComponent.LS_PAGE_SIZE));
            if (this.page_size_options.indexOf(ps) !== -1) { this.pageobj.count = ps; }
        } catch (e) { /* 私密模式等拿不到 localStorage，用預設就好 */ }
        this.build_attributes();
        this.init_list();
    }

    // Esc 關對照視窗、← → 換上一張/下一張；Swal 開著時不動（不然 Esc 會把 Swal 跟視窗一起關掉，不知道按到了什麼）
    private swal_open(): boolean { return !!document.querySelector('.swal2-container'); }
    @HostListener('document:keydown.escape')
    on_escape() { if (this.preview && !this.swal_open()) { this.close_preview(); } }
    @HostListener('document:keydown.arrowleft')
    on_left() { if (this.preview && !this.swal_open()) { this.preview_nav(-1); } }
    @HostListener('document:keydown.arrowright')
    on_right() { if (this.preview && !this.swal_open()) { this.preview_nav(1); } }

    // 搜尋條件是否已離開預設值（「重設」按鈕亮起來提醒現在是篩過的結果）
    private default_month = '';
    get filters_dirty(): boolean {
        return this.vid !== '-1' || this.current_month !== this.default_month || this.current_monthed !== this.default_month
            || !!this.search_model || !!this.search_studio_name || this.search_trasnfer_status !== ''
            || this.search_withdrawal_status !== '-1' || this.search_invoice_ok !== '-1'
            || this.search_health_fee !== '' || this.search_account_memo !== '';
    }

    // 搜尋條件回預設：上個月、全部團隊、不限
    reset_filters(reload = true) {
        this.vid = '-1';
        this.default_month = moment().subtract(1, 'month').format('YYYY-MM');
        this.current_month = this.default_month;
        this.current_monthed = this.current_month;
        this.search_model = '';
        this.search_studio_name = '';
        this.search_trasnfer_status = '';
        this.search_withdrawal_status = '-1';
        this.search_invoice_ok = '-1';
        this.search_health_fee = '';
        this.search_account_memo = '';
        if (reload) { this.init_list(); }
    }

    // 空結果時的捷徑：拿掉月份條件查全部
    search_all_months() {
        this.current_month = '';
        this.current_monthed = '';
        this.init_list();
    }

    build_attributes() {
        // Rtable.structure 是 static 共用物件，其他頁會改 type，這裡先押回 txt
        Rtable.structure.type = 'txt';
        // 操作欄放前面：對帳的人是「找到列 → 看金額 → 發票 → 核實 → 付款」，不該捲到最右邊才看得到按鈕
        const core: any = {
            name: { name: '淘妹名稱', type: 'opt' },
            vendor_name: { name: '團隊', sort: 1 },
            studio_name: { name: '工作室', sort: 1 },
            actual_withdrawal_time: { name: '結算月份', sort: 1 },
            real_money: { name: '實際結算金額', type: 'num', unit: '元', sort: 1 },
            opt: { name: '提領方式', type: 'opt' },
            invoice: { name: '發票', type: 'opt' },
            invoice_ok: { name: '發票核實', type: 'opt', sort: 1 },
            actual_withdrawal_status: { name: '付款狀態', type: 'opt', sort: 1 },
        };
        const detail: any = {
            general_money: { name: '一般收入', type: 'num' },
            active_money: { name: '活動收入', type: 'num' },
            bonus_money: { name: '獎金收入', type: 'num' },
            pre_cut_diamond: { name: '預扣收入', type: 'num' },
            accumulation_money: { name: '之前累積', type: 'num' },
            total_money: { name: '收益金額', type: 'num', unit: '元' },
            fee: { name: '代扣稅額', type: 'num', unit: '元' },
            healthy_fee: { name: '代扣健保', type: 'num', unit: '元' },
            start_dt: { name: '計算日期' },
            end_dt: { name: '結算日期' },
            bvalue: { name: '收款行通匯代碼' },
            baccount: { name: '收款人帳號', type: 'opt' },
            baccountname: { name: '收款戶名' },
            id_name: { name: '姓名' },
            id_number: { name: '身分證' },
            addr: { name: '地址' },
        };
        this.posts_attributes = Rtable.init(this.show_all_cols ? { ...core, ...detail } : core);
    }

    toggle_cols() {
        this.show_all_cols = !this.show_all_cols;
        try { localStorage.setItem(StudioMemberComponent.LS_COLS, this.show_all_cols ? 'all' : 'core'); } catch (e) { }
        this.build_attributes();
        this.apply_sort_and_page();
    }

    set_page_size(n: any) {
        this.pageobj.count = Number(n) || 20;
        this.pageobj.page = 0;
        try { localStorage.setItem(StudioMemberComponent.LS_PAGE_SIZE, String(this.pageobj.count)); } catch (e) { }
        this.apply_sort_and_page();
    }

    private busy_start() { this.pending++; }
    private busy_end() { this.pending = Math.max(0, this.pending - 1); }

    // ---- 讀列表 ----
    init_list(keep_page = false) {
        if (!keep_page) { this.pageobj.page = 0; }
        this.close_preview(); // 列表要換了，視窗裡那筆會變成舊物件
        // 只填結束月份就當單月查
        if (!this.current_month && this.current_monthed) { this.current_month = this.current_monthed; }
        // 起訖倒置就對調，不然後端 range 查到 0 筆卻沒任何提示
        if (this.current_month && this.current_monthed && this.current_monthed < this.current_month) {
            const t = this.current_month; this.current_month = this.current_monthed; this.current_monthed = t;
        }
        const seq = ++this.req_seq;
        this.busy_start();
        this.load_msg = '';
        const body: any = {
            vid: this.vid,
            month: this.current_month,
            monthEnd: this.current_monthed || this.current_month,
            search_model: this.search_model,
            search_studio_name: this.search_studio_name,
            search_trasnfer_status: this.search_trasnfer_status,
            search_withdrawal_status: this.search_withdrawal_status,
            search_invoice_ok: this.search_invoice_ok,
            search_health_fee: this.search_health_fee,
            search_account_memo: this.search_account_memo,
        };
        this.httpClient.post<any>('/get_studio_member.php', body).subscribe(res => {
            this.busy_end();
            if (seq !== this.req_seq) { return; } // 已經有更新的查詢送出去了，這個舊回應丟掉
            this.loaded_once = true;
            if (res.status) {
                this.data_all = Array.isArray(res.data) ? res.data : [];
                // 會計備註藏在 memo JSON 裡，這裡解一次存到列上，模板不要每次變更偵測都 JSON.parse
                this.data_all.forEach(r => { r._acc_memo = this.parse_accounting_memo(r); r._busy = false; });
                this.collect_vendor_options(this.data_all);
            } else {
                this.data_all = [];
                this.load_msg = res.message || '讀取失敗';
            }
            this.compute_summary();
            this.apply_sort_and_page();
        }, () => {
            this.busy_end();
            if (seq !== this.req_seq) { return; }
            this.loaded_once = true;
            this.data_all = [];
            this.load_msg = '連線失敗，請稍後再試';
            this.compute_summary();
            this.apply_sort_and_page();
        });
    }

    collect_vendor_options(rows: any[]) {
        rows.forEach(r => {
            const id = String(r.vid);
            if (!this.vendor_options.find(v => v.id === id)) {
                this.vendor_options.push({ id, name: r.vendor_name || ('團隊 ' + id) });
            }
        });
    }

    compute_summary() {
        const s = { no_invoice: 0, awaiting: 0, ready: 0, paid: 0, failed: 0, stuck: 0, unpaid_rows: 0, unpaid_sum: 0, ready_sum: 0, failed_sum: 0, paid_sum: 0, accum_rows: 0 };
        this.data_all.forEach(r => {
            const p = Number(r.actual_withdrawal_status);
            const verified = Number(r.invoice_ok) === 1;
            const payable = this.payable_ts(r);
            const t = Number(r.transfer_status);
            const m = Number(r.real_money) || 0;
            // 金額只算真的會出帳的列（匯款 1,2 / 領現 5）；累積(3,4)的金額會滾進之後某一期的「之前累積」，加進來會重複計
            if (!payable) { if (t === 3 || t === 4) { s.accum_rows++; } return; }
            if (p === 1) { s.paid++; s.paid_sum += m; return; }
            // 還沒付款成功的都算未付（付款失敗的錢還是欠著，只是預設不進匯款單，要勾「含已付款」或在列上改）
            s.unpaid_rows++; s.unpaid_sum += m;
            if (p === 2) { if (verified) { s.failed++; s.failed_sum += m; } else { s.stuck++; } return; }
            if (verified) { s.ready++; s.ready_sum += m; } else if (r.invoice) { s.awaiting++; } else { s.no_invoice++; }
        });
        this.summary = s;
    }

    // 前端排序 + 分頁（後端不分頁）
    apply_sort_and_page() {
        const col = this.sort_obj.column;
        const desc = this.sort_obj.sort === '0';
        const rows = this.data_all.slice();
        if (col) {
            rows.sort((a, b) => {
                const av = a[col], bv = b[col];
                const an = Number(av), bn = Number(bv);
                let r: number;
                if (av !== '' && bv !== '' && av !== null && bv !== null && !isNaN(an) && !isNaN(bn)) {
                    r = an - bn;
                } else {
                    r = String(av ?? '').localeCompare(String(bv ?? ''), 'zh-Hant');
                }
                return desc ? -r : r;
            });
        }
        this.pageobj.total_count = rows.length;
        const max_page = Math.max(Math.ceil(rows.length / this.pageobj.count) - 1, 0);
        if (this.pageobj.page > max_page) { this.pageobj.page = max_page; }
        const start = this.pageobj.page * this.pageobj.count;
        this.data = rows.slice(start, start + this.pageobj.count);
        this.posts_input = {
            attribute: this.posts_attributes,
            content: this.data,
            sort_obj: { ...this.sort_obj }, // clone，r-table 會直接改它
        };
        this.pageobjstr = JSON.parse(JSON.stringify(this.pageobj));
    }

    on_sort(sort_obj: any) {
        if (sort_obj && sort_obj.column) {
            this.sort_obj = { column: sort_obj.column, sort: String(sort_obj.sort) };
        }
        this.pageobj.page = 0;
        this.apply_sort_and_page();
    }

    callback(pageobj: any) {
        this.pageobj.page = Number(pageobj.page) || 0;
        this.apply_sort_and_page();
    }

    // ---- 匯出（GET，開新視窗；只匯出 invoice_ok = 1 的列）----
    // getter 每次變更偵測都會算，所以不放 Date.now() 之類會一直變的東西（下載回應本來就不會被快取）
    export_url(file: string): string {
        let url = this.preurl + '/' + file + '?token=' + Dep.token + '&month=' + this.current_month;
        if (this.vid && this.vid !== '-1') { url += '&vid=' + this.vid; }
        if (this.search_studio_name) { url += '&studio_name=' + encodeURIComponent(this.search_studio_name); }
        if (this.include_paid) { url += '&all=1'; }
        return url;
    }
    get export_wd_url(): string { return this.export_url('export_studio_wd.php'); }
    get export_cash_url(): string { return this.export_url('export_studio_wd_cash.php'); }

    // ---- 匯入付款狀態 Excel（multipart，token 放表單欄位；照既有 import_actual_withdrawal_status_excel 的送法）----
    upload_excel(event: any) {
        const input = event.target;
        const file = input.files && input.files[0];
        input.value = '';
        if (!file) { return; }
        const that = this;
        this.busy_start();
        const body = new FormData();
        body.append('token', Dep.token);
        body.append('file[]', file);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', environment.serverurl + '/import_studio_withdrawal_status_excel.php', true);
        xhr.onloadend = function () {
            that.busy_end();
            let res: any = null;
            try { res = JSON.parse(this.response); } catch (e) { res = null; }
            if (!res) {
                Swal.fire({ title: '上傳失敗', icon: 'error', html: '伺服器回應無法解析' + (this.response ? '<pre class="text-left small mt-2">' + that.esc(String(this.response).slice(0, 300)) + '</pre>' : '') });
                return;
            }
            const d = res.data || {};
            const skip_lines: string[] = [];
            const add = (label: string, arr: any[]) => {
                // id 是從 Excel A 欄原樣讀出來的字串，要跳脫再放進 html
                if (Array.isArray(arr) && arr.length > 0) { skip_lines.push(that.esc(label) + '：' + arr.map(x => that.esc(x)).join(', ')); }
            };
            add('非工作室列', d.skip_not_studio);
            add('發票未核實', d.skip_invoice);
            add('查無此筆', d.skip_notfound);
            add('該列沒有狀態欄', d.skip_no_status);
            add('狀態值不是 0/1/2', d.skip_bad_status);
            Swal.fire({
                title: res.status ? '匯入完成' : '匯入失敗',
                icon: res.status ? 'success' : 'error',
                html: '<div>' + that.esc(res.message || '') + '</div>' +
                    (skip_lines.length ? '<div class="text-left small mt-2" style="white-space:pre-line">跳過的 vendor_member_withdrawal.id：\n' + skip_lines.join('\n') + '</div>' : ''),
            });
            if (res.status) { that.init_list(true); }
        };
        xhr.send(body);
    }

    // ---- 逐列操作（都走 set_studio_member.php）----
    // row._busy：該列送出中，按鈕全部鎖住避免連點；整頁的 loading 遮罩照常顯示
    private post_set(body: any, row: any, ok: (res: any) => void) {
        if (row && row._busy) { return; }
        if (row) { row._busy = true; }
        this.busy_start();
        this.httpClient.post<any>('/set_studio_member.php', body).subscribe(res => {
            this.busy_end();
            if (row) { row._busy = false; }
            if (res.status) {
                ok(res);
            } else {
                Swal.fire({ title: res.message || '修改失敗', icon: 'error' });
            }
        }, () => {
            this.busy_end();
            if (row) { row._busy = false; }
            Swal.fire({ title: '連線失敗', icon: 'error' });
        });
    }

    // ---- 後端 804b044d 的閘門，前端先照同一套鎖（後端一定會再擋一次）----
    // 「還在可編輯階段」＝尚未付款(aws=0)；付款成功 / 付款失敗後，提領方式、發票、核實都凍結
    editable(row: any): boolean { return Number(row.actual_withdrawal_status) === 0; }
    // 會出帳的提領方式：1/2 匯款、5 領現；累積 3/4、試算 0、鎖定 -1 不出帳也不能付款
    payable_ts(row: any): boolean { return [1, 2, 5].indexOf(Number(row.transfer_status)) !== -1; }
    // 提領方式為什麼不能切；'' = 可以
    transfer_lock_reason(row: any): string {
        if (!this.payable_ts(row)) { return ''; } // 這種列本來就不顯示按鈕，用徽章
        return this.editable(row) ? '' : '已進付款流程，不可變更提領方式';
    }
    // 發票 / 核實為什麼不能動；'' = 可以
    invoice_lock_reason(row: any): string {
        return this.editable(row) ? '' : (Number(row.actual_withdrawal_status) === 1 ? '已付款，發票不可更改' : '付款失敗中，發票不可更改');
    }
    // invoice 要是 http(s) 網址才拿來當 <img src>
    is_http_url(u: any): boolean { return typeof u === 'string' && /^https?:\/\//i.test(u); }

    // type 0 提領方式：1 匯款 / 5 領現
    set_transfer(row: any, status: number) {
        if (Number(row.transfer_status) === status || (status === 1 && Number(row.transfer_status) === 2)) { return; }
        const lock = this.transfer_lock_reason(row);
        if (lock || !this.payable_ts(row)) {
            Swal.fire({ title: '此列目前狀態不可變更提領方式', text: lock || '累積 / 鎖定 / 試算中的列不能切換', icon: 'warning' });
            return;
        }
        Swal.fire({
            title: '確定改為「' + (status === 5 ? '領現' : '匯款') + '」？',
            text: '會影響這一筆出現在匯款單還是領現單',
            showCancelButton: true, confirmButtonText: '更改', cancelButtonText: '取消',
        }).then(r => {
            if (!r.isConfirmed) { return; }
            this.post_set({ type: 0, data: { id: row.id }, status }, row, () => {
                Swal.fire({ title: '已修改', icon: 'success', timer: 1200, showConfirmButton: false });
                this.init_list(true);
            });
        });
    }

    // 付款狀態能不能從目前值改成 target：規則與一般對帳頁相同
    //   尚未付款(0) → 三種都可；付款失敗(2) → 只能改成付款成功；付款成功(1) → 鎖死（要改只能走 Excel 匯入）
    payment_allowed(row: any, target: number): boolean {
        const cur = Number(row.actual_withdrawal_status);
        if (cur === 1) { return false; }
        if (cur === 2) { return target === 1; }
        return true;
    }

    // 這列的付款狀態目前為什麼不能改；'' = 可以改（順序＝後端閘門順序）
    payment_lock_reason(row: any): string {
        if (Number(row.actual_withdrawal_status) === 1) { return '已付款成功，不可更改'; }
        if (!this.payable_ts(row)) { return this.transfer_label(row.transfer_status) + '列不出帳，不可付款'; }
        if (Number(row.invoice_ok) !== 1) {
            // 付款失敗後發票被凍結（後端 aws≠0 不給改），但付款又要 invoice_ok=1：前端無解，直接講明
            if (!this.editable(row)) { return '付款失敗且發票未核實：此列已凍結，請找工程處理'; }
            return row.invoice ? '待會計核實發票' : '待上傳並核實發票';
        }
        return '';
    }

    // type 1 付款狀態：0 尚未付款 / 1 付款成功 / 2 付款失敗（發票沒核實後端會擋，這裡先提示）
    set_payment(row: any, status: number) {
        if (Number(row.actual_withdrawal_status) === status) { return; }
        if (!this.payment_allowed(row, status)) {
            Swal.fire({ title: '不能這樣改', text: Number(row.actual_withdrawal_status) === 1 ? '已付款成功的紀錄不可再更改' : '付款失敗只能改成付款成功', icon: 'warning' });
            return;
        }
        if (!this.payable_ts(row)) {
            Swal.fire({ title: '此列非可付款狀態', text: '只有匯款 / 領現的列會出帳；累積列的金額會滾進之後某一期', icon: 'warning' });
            return;
        }
        if (Number(row.invoice_ok) !== 1) {
            Swal.fire({ title: '發票尚未核實', text: '請先上傳發票並由會計核實，才能更改付款狀態', icon: 'warning' });
            return;
        }
        Swal.fire({
            title: '確定更改付款狀態？',
            text: '改為「' + this.payment_label(status) + '」。同一用戶更早的紀錄也會一起改（遇累積已匯停止）',
            showCancelButton: true, confirmButtonText: '更改', cancelButtonText: '取消',
        }).then(r => {
            if (!r.isConfirmed) { return; }
            this.post_set({ type: 1, data: { id: row.id }, status }, row, () => {
                Swal.fire({ title: '已修改', icon: 'success', timer: 1200, showConfirmButton: false });
                this.init_list(true);
            });
        });
    }

    // type 2 上傳發票（base64，含 data:image/... 前綴；上傳後 invoice_ok 一律歸 0 要重新核實）
    // 後端 upload_for_b64 只收 jpg/jpeg/png/gif/webp，iPhone 的 HEIC 會回「上傳失敗」，這裡先用副檔名擋掉並講清楚
    upload_invoice(event: any, row: any) {
        const input = event.target;
        const file: File = input.files && input.files[0];
        input.value = ''; // 先重設：不論成功失敗，同一張圖再選一次都要能觸發 change
        if (!file) { return; }
        if (!this.editable(row)) {
            Swal.fire({ title: '已付款的紀錄不可更改發票', text: this.invoice_lock_reason(row), icon: 'warning' });
            return;
        }
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (StudioMemberComponent.INVOICE_EXT.indexOf(ext) === -1) {
            Swal.fire({ title: '圖片格式不支援', text: '只收 jpg / jpeg / png / gif；webp 與 iPhone 的 HEIC 請先轉成 jpg 或 png', icon: 'error' });
            return;
        }
        if (file.size > 4 * 1024 * 1024) {
            Swal.fire({ title: '圖片太大', text: '請壓到 4MB 以內再上傳', icon: 'error' });
            return;
        }
        const reader = new FileReader();
        // 讀檔失敗一定要走 onerror 停下來；若用 onloadend，失敗時 result 是 null，送出去會被後端當成「清空發票」
        reader.onerror = () => {
            Swal.fire({ title: '讀取圖片失敗', text: '請重新選擇檔案', icon: 'error' });
        };
        reader.onload = (e: any) => {
            const b64 = e.target.result;
            if (typeof b64 !== 'string' || b64.indexOf('data:image/') !== 0) {
                Swal.fire({ title: '讀取圖片失敗', text: '檔案內容不是圖片', icon: 'error' });
                return;
            }
            const was_ok = Number(row.invoice_ok) === 1;
            const go = () => {
                this.post_set({ type: 2, data: { id: row.id }, pic: b64 }, row, res => {
                    row.invoice = res.data?.invoice ?? '';
                    row.invoice_ok = res.data?.invoice_ok ?? 0;
                    this.compute_summary();
                    Swal.fire({ title: '發票已上傳', text: '待會計核實', icon: 'success', timer: 1500, showConfirmButton: false });
                });
            };
            if (was_ok) {
                Swal.fire({
                    title: '這筆發票已核實，要換掉嗎？',
                    text: '換新圖後會變回「未核實」，需要重新核對',
                    showCancelButton: true, confirmButtonText: '換掉', cancelButtonText: '取消',
                }).then(r => { if (r.isConfirmed) { go(); } });
            } else {
                go();
            }
        };
        reader.readAsDataURL(file);
    }

    // type 2 pic 空字串 = 清空發票
    clear_invoice(row: any) {
        if (!this.editable(row)) {
            Swal.fire({ title: '已付款的紀錄不可更改發票', text: this.invoice_lock_reason(row), icon: 'warning' });
            return;
        }
        Swal.fire({
            title: '確定移除這筆發票？',
            text: '移除後付款狀態會被鎖住，需重新上傳並核實',
            showCancelButton: true, confirmButtonText: '移除', cancelButtonText: '取消',
        }).then(r => {
            if (!r.isConfirmed) { return; }
            this.post_set({ type: 2, data: { id: row.id }, pic: '' }, row, res => {
                row.invoice = '';
                row.invoice_ok = res.data?.invoice_ok ?? 0;
                this.compute_summary();
                if (this.preview === row) { this.close_preview(); }
            });
        });
    }

    // type 3 發票核實 0 / 1
    set_invoice_ok(row: any, ok: number) {
        if (Number(row.invoice_ok) === ok) { return; }
        if (!this.editable(row)) {
            Swal.fire({ title: '已付款的紀錄不可更改發票', text: this.invoice_lock_reason(row), icon: 'warning' });
            return;
        }
        if (ok === 1 && !row.invoice) {
            Swal.fire({ title: '尚未上傳發票', icon: 'warning' });
            return;
        }
        Swal.fire({
            title: ok === 1 ? '確認發票已核對？' : '取消核實？',
            text: ok === 1
                ? '請確認發票金額與實際結算金額 ' + this.fmt(row.real_money) + ' 元相符'
                : '取消後這筆會從匯款單 / 領現單消失，付款狀態也不能改',
            showCancelButton: true, confirmButtonText: ok === 1 ? '確認核實' : '取消核實', cancelButtonText: '返回',
        }).then(r => {
            if (!r.isConfirmed) { return; }
            this.post_set({ type: 3, data: { id: row.id }, invoice_ok: ok }, row, res => {
                row.invoice_ok = res.data?.invoice_ok ?? ok;
                this.compute_summary();
            });
        });
    }

    // ---- 發票對照視窗 ----
    open_preview(row: any) {
        if (row && row.invoice) { this.preview = row; }
    }
    close_preview() { this.preview = null; }
    // 視窗只在「這一頁有發票的列」之間切換，順序跟表格一樣；會計可以一張接一張核對不用關視窗
    preview_rows(): any[] { return this.data.filter(r => r.invoice); }
    preview_pos(): { i: number, n: number } { const rows = this.preview_rows(); return { i: rows.indexOf(this.preview), n: rows.length }; }
    preview_nav(dir: number) {
        const rows = this.preview_rows();
        if (!rows.length) { return; }
        let i = rows.indexOf(this.preview);
        i = i === -1 ? 0 : (i + dir + rows.length) % rows.length;
        this.preview = rows[i];
    }

    // ---- 顯示用 ----
    transfer_label(s: any): string {
        switch (Number(s)) {
            case 1: case 2: return '匯款';
            case 3: return '累積未匯';
            case 4: return '累積已匯';
            case 5: return '領現';
            case -1: return '鎖定';
            case 0: return '試算中';
            default: return String(s ?? '');
        }
    }
    payment_label(s: any): string {
        switch (Number(s)) {
            case 0: return '尚未付款';
            case 1: return '付款成功';
            case 2: return '付款失敗';
            default: return String(s ?? '');
        }
    }
    payment_badge_cls(s: any): string {
        switch (Number(s)) {
            case 1: return 'badge-success';
            case 2: return 'badge-danger';
            default: return 'badge-secondary';
        }
    }
    fmt(n: any): string {
        const v = Number(n);
        return isNaN(v) ? String(n ?? '') : v.toLocaleString('zh-TW');
    }
    esc(s: any): string {
        return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));
    }
    parse_accounting_memo(row: any): string {
        // memo 是 JSON 字串（陣列，[0].accounting_memo 是會計備註），壞掉就不顯示；讀列表時呼叫一次存到 row._acc_memo
        try {
            const m = typeof row.memo === 'string' ? JSON.parse(row.memo) : row.memo;
            const first = Array.isArray(m) ? m[0] : m;
            return first?.accounting_memo || '';
        } catch (e) { return ''; }
    }
}
